import React from 'react';
import { cj } from 'src/utils/classNames';
import { AprDetails } from 'src/hooks/earn/types';
import { PointsMono } from '@velocity-exchange/icons';
import { ApyChunk } from 'src/hooks/useApyBreakdownTooltipData';
import Tooltip from '../primitives/Tooltip/Tooltip';
import { WithChildren, WithClassName } from 'src/utils/UtilityTypes';
import { getColorVar } from 'src/utils/theme/colorVars';

export interface ApyBreakdownTooltipProps extends WithChildren, WithClassName {
	apyChunks: ApyChunk[];
	showTooltip: boolean;
}

const ApyBreakdownRow = ({
	source,
	apy,
	hideYield = false,
	helperText,
	onlyText,
	icon,
	type,
}: {
	source: string;
	apy: number;
	hideYield?: boolean;
	helperText?: string | React.ReactNode;
	icon?: string;
	onlyText?: boolean;
	type?: AprDetails['type'];
}) => {
	if (onlyText && helperText) {
		return <div className="text-text-secondary">{helperText}</div>;
	}

	const isBorrow = type === 'borrow';

	return (
		<li className="text-text-default">
			<div className={cj('flex w-full items-center justify-between')}>
				<span className="typo-body-xs">{source}</span>
				{!hideYield && (
					<div className="flex items-center gap-1">
						{icon && (
							<img
								src={icon}
								alt={`${source} icon`}
								className="mr-1 inline-block size-4"
							/>
						)}
						<span
							className={cj(
								'typo-label-xs',
								isBorrow
									? 'text-star-yellow'
									: 'text-text-positive-green-button'
							)}
						>
							{!isFinite(apy) || apy > 1000
								? '> 1,000%'
								: type === 'lend-oi-reward'
									? `≥ ${apy.toFixed(2)}%`
									: `${apy.toFixed(2)}%`}
						</span>
					</div>
				)}
			</div>
			{helperText && (
				<div className="typo-label-xs text-text-secondary">{helperText}</div>
			)}
		</li>
	);
};

const ApyChunkComponent = ({ title, apyBreakdown }: ApyChunk) => {
	if (!apyBreakdown) {
		return null;
	}
	const hasMultipleApyBreakdown = apyBreakdown.length > 1;
	const isBorrow = apyBreakdown.some((detail) => detail?.type === 'borrow');
	const subTotalApy = apyBreakdown.reduce((acc, detail) => {
		if (detail?.type === 'borrow') {
			return acc - (detail?.apy ?? 0);
		}
		return acc + (detail?.apy ?? 0);
	}, 0);
	const isPositive = isBorrow ? subTotalApy > 0 : subTotalApy >= 0;

	if (hasMultipleApyBreakdown) {
		return (
			<div className="flex flex-col gap-y-md">
				<div className="flex w-full items-center justify-between">
					<span className="typo-label-sm text-text-default">{title}</span>
					<span
						className={cj(
							'typo-label-sm',
							isPositive
								? 'text-text-positive-green-button'
								: 'text-star-yellow'
						)}
					>
						{!isFinite(subTotalApy) || Math.abs(subTotalApy) > 1000
							? '> 1,000%'
							: `${Math.abs(subTotalApy).toFixed(2)}%`}
					</span>
				</div>
				<ul className="flex list-disc flex-col gap-y-sm pl-3">
					{apyBreakdown.map((detail) => {
						return (
							<ApyBreakdownRow
								key={detail?.source}
								source={detail?.source}
								apy={detail?.apy}
								hideYield={detail?.hideYield}
								helperText={detail?.helperText}
								type={detail?.type}
							/>
						);
					})}
				</ul>
			</div>
		);
	}

	return (
		<div className="flex w-full items-center justify-between">
			<span className="typo-label-sm">{title}</span>
			<span
				className={cj(
					'typo-label-sm',
					isPositive ? 'text-text-positive-green-button' : 'text-star-yellow'
				)}
			>
				{!isFinite(subTotalApy) || Math.abs(subTotalApy) > 1000
					? '> 1,000%'
					: `${subTotalApy.toFixed(2)}%`}
			</span>
		</div>
	);
};

const ApyBreakdownTooltipV2 = ({
	apyChunks,
	showTooltip,
	children,
	className,
}: ApyBreakdownTooltipProps) => {
	if (!showTooltip) {
		return children;
	}

	const nonPerpCollateralApyChunks = apyChunks.filter(
		(chunk) => !chunk.isPerpCollateralReward
	);

	const needCombinedApy =
		nonPerpCollateralApyChunks.filter(
			(chunk) => chunk.apyBreakdown !== undefined
		).length > 1;

	const nonPerpCollateralCombinedApy = nonPerpCollateralApyChunks.reduce(
		(acc, chunk) => {
			if (!chunk.apyBreakdown) {
				return acc;
			}
			return (
				acc +
				chunk.apyBreakdown.reduce((detailAcc, detail) => {
					return detailAcc + (detail?.apy ?? 0);
				}, 0)
			);
		},
		0
	);

	const isPositive = nonPerpCollateralCombinedApy >= 0;

	const perpsCollateralRewardDetails = apyChunks
		.find((chunk) => chunk.isPerpCollateralReward)
		?.apyBreakdown?.find(
			(detail) => detail?.type === 'lend-oi-reward'
		)?.perpsCollateralReward;

	const content = (
		<div className="flex flex-col gap-y-md">
			<div className="flex flex-col gap-y-sm">
				{nonPerpCollateralApyChunks.map((chunk, index) => (
					<ApyChunkComponent
						key={`${chunk.title}-${index}`}
						title={chunk.title}
						apyBreakdown={chunk.apyBreakdown}
					/>
				))}
			</div>

			{needCombinedApy && (
				<div className="flex flex-col gap-y-md border-t border-text-secondary pt-md">
					<div className="flex w-full items-center justify-between">
						<span className="typo-label-sm">Total</span>
						<span
							className={cj(
								'typo-label-sm',
								isPositive
									? 'text-text-positive-green-button'
									: 'text-star-yellow'
							)}
						>
							{!isFinite(nonPerpCollateralCombinedApy) ||
							Math.abs(nonPerpCollateralCombinedApy) > 1000
								? '> 1,000%'
								: `${nonPerpCollateralCombinedApy.toFixed(2)}%`}
						</span>
					</div>
				</div>
			)}

			{perpsCollateralRewardDetails && (
				<div
					className={cj(
						'flex flex-col gap-y-2 [&:not(:first-child)]:-mt-1 [&:not(:first-child)]:border-t [&:not(:first-child)]:border-text-disabled [&:not(:first-child)]:pt-2'
					)}
				>
					<div className="flex items-center gap-1">
						<PointsMono color={getColorVar('purple-link')} />
						<span className="typo-label-sm">Perps Collateral Incentives</span>
					</div>

					<div className="mt-1 flex items-center justify-between gap-2">
						<div className="flex flex-col gap-2xs">
							<span className="typo-label-sm">
								{perpsCollateralRewardDetails.rewardMarketSymbol} Incentives APY
							</span>
							<span className="typo-label-xs text-text-secondary">
								{perpsCollateralRewardDetails.dailyReward.toFixed(0)}{' '}
								{perpsCollateralRewardDetails.rewardMarketSymbol} Daily
							</span>
						</div>
						<span
							className={cj(
								'typo-label-sm',
								perpsCollateralRewardDetails.apy >= 0
									? 'text-text-positive-green-button'
									: 'text-star-yellow'
							)}
						>
							{perpsCollateralRewardDetails.apy.toFixed(2)}%
						</span>
					</div>

					<span className="typo-label-xs text-text-secondary">
						Use {perpsCollateralRewardDetails.collateralMarketSymbol} as perps
						collateral to earn {perpsCollateralRewardDetails.rewardMarketSymbol}{' '}
						incentives.{' '}
						<a
							className="cursor-pointer font-bold"
							target="_blank"
							href={perpsCollateralRewardDetails.learnMoreLink}
							rel="noreferrer"
						>
							Learn more
						</a>
					</span>
				</div>
			)}
		</div>
	);

	return (
		<Tooltip
			content={content}
			contentClassName="w-[300px]"
			className={className}
		>
			{children}
		</Tooltip>
	);
};

export default ApyBreakdownTooltipV2;
